import { lazy, useCallback, type ComponentType } from 'react';

import AssignmentTurnedInIcon from '@mui/icons-material/AssignmentTurnedIn';
import type { PaletteMode } from '@mui/material';
import Box from '@mui/material/Box';
import { DashboardApp, type DashboardShellConfig } from '@tide/dashboard-shell';

import Loadable from 'components/Loadable';
import LogoIcon from 'components/logo/LogoIcon';
import LogoMain from 'components/logo/LogoMain';
import Notistack from 'components/third-party/Notistack';
import useConfig from 'hooks/useConfig';

import { portalDashboardAuthAdapter } from './PortalDashboardAuth';
import { createPortalDashboardTheme } from './PortalDashboardTheme';
import ReactRouterRouteAdapter from './ReactRouterRouteAdapter';

const OrderDashboard = Loadable(lazy(() => import('pages/order/OrderDashboard')));
const EditOrder = Loadable(lazy(() => import('pages/order/EditOrder')));
const MaintenanceError = Loadable(lazy(() => import('pages/maintenance/404')));
const MaintenanceError500 = Loadable(lazy(() => import('pages/maintenance/500')));

type PortalRouteDefinition = {
  dashboardPath: string;
  reactRouterPath: string;
  component: ComponentType;
};

const withPortalProviders = ({ component: Page, reactRouterPath }: PortalRouteDefinition): ComponentType => {
  const PortalRoute = () => (
    <ReactRouterRouteAdapter path={reactRouterPath}>
      <Notistack>
        <Page />
      </Notistack>
    </ReactRouterRouteAdapter>
  );

  return PortalRoute;
};

const routes: PortalRouteDefinition[] = [
  {
    dashboardPath: '/',
    reactRouterPath: '/',
    component: OrderDashboard
  },
  {
    dashboardPath: '/orders/$orderId/questions',
    reactRouterPath: '/orders/:orderId/questions',
    component: EditOrder
  },
  {
    dashboardPath: '/maintenance/404',
    reactRouterPath: '/maintenance/404',
    component: MaintenanceError
  },
  {
    dashboardPath: '/maintenance/500',
    reactRouterPath: '/maintenance/500',
    component: MaintenanceError500
  }
];

const logo = (
  <Box
    sx={{
      alignItems: 'center',
      display: 'flex',
      maxWidth: '100%',
      minWidth: 0,
      overflow: 'hidden',
      '& svg': {
        display: 'block',
        height: 32,
        marginLeft: '0 !important',
        maxWidth: '100%',
        width: 'auto'
      }
    }}
  >
    <LogoMain />
  </Box>
);

const logoMark = (
  <Box
    sx={{
      alignItems: 'center',
      display: 'flex',
      height: 36,
      justifyContent: 'center',
      overflow: 'hidden',
      width: 36,
      '& svg': {
        display: 'block',
        height: 36,
        width: 36
      }
    }}
  >
    <LogoIcon />
  </Box>
);

const dashboardConfig: DashboardShellConfig = {
  serviceName: 'Geodesys Portal',
  serviceShortName: 'GP',
  homeTitle: 'Orders',
  homeSubtitle: 'Manage active, on hold, and historic order work',
  logo,
  logoMark,
  maxContentWidth: 'xl',
  navigation: [
    {
      label: 'Work',
      items: [
        {
          id: 'orders',
          icon: <AssignmentTurnedInIcon />,
          label: 'Orders',
          path: '/',
          subtitle: 'Order queues and status workflow'
        }
      ]
    }
  ],
  routes: routes.map((route) => ({
    path: route.dashboardPath,
    component: withPortalProviders(route),
    access: {
      requireAuthenticated: true
    }
  }))
};

export default function PortalDashboard() {
  const { fontFamily, presetColor, themeDirection } = useConfig();

  const themeFactory = useCallback(
    (mode: PaletteMode) => createPortalDashboardTheme({ fontFamily, mode, presetColor, themeDirection }),
    [fontFamily, presetColor, themeDirection]
  );

  return <DashboardApp authAdapter={portalDashboardAuthAdapter} config={dashboardConfig} themeFactory={themeFactory} />;
}
